import React, { useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  ButtonIcon,
  Select,
  Datepicker,
  Radio,
  Checkbox,
} from 'components';
import { globalPropTypes } from 'helpers';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  loadGlobalCisOrganizationsAttributesCreator,
  loadIpmaCisOrganizationCertificationBodyLevelDomainsCreator,
  removeIpmaCisOrganizationCertificationBodyLevelDomainsCreator,
  setIpmaCisEditErrorsCreator,
  updateIpmaCisCreator,
} from '../../../../../store/actionsCreators';
import {
  organizationIndustryOptions,
  organizationSizeOptions,
  organizationStatusOptions,
  organizationTypeOptions,
} from '../../../../../shared/Cis/CisOptions';
import moment from 'moment';
import DebouncedSelect from '../../../../../components/DebounceSelect/DebounceSelect';
import { getCisUsersOptions } from '../../../../../api/services/user';

const EditOrganization = ({
  organization,
  errors,
  updateOrganization,
  removeError,
  certificationBodies,
  levelDomains,
  countries,
  loadOrganizationsAttributes,
  certificationBodyLevelDomains,
  loadCertificationBodyLevelDomains,
  removeCertificationBodyLevelDomains,
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  useEffect(() => {
    if (organization.id) {
      loadCertificationBodyLevelDomains(organization.id);
    }

    return () => {
      removeCertificationBodyLevelDomains();
    };
  }, [organization.id]);

  useEffect(() => {
    if (organization) {
      dispatch(setEditDataCreator(organization));
    }
  }, [organization]);

  useEffect(() => {
    if (certificationBodyLevelDomains) {
      dispatch(
        updateFieldCreator({
          name: 'certificationBodyLevelDomains',
          value: certificationBodyLevelDomains,
        }),
      );
    }
  }, [certificationBodyLevelDomains]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeNestedNative = (group, name, errorName) => (e) => {
    dispatch(
      updateFieldCreator({ name: group, value: { ...state[group], [name]: e.target.value } }),
    );
    if (errors[errorName]) dispatch(removeError(clearErrors(errors, errorName)));
  };

  const handleChangeNestedSelect = (group, name, errorName) => (option) => {
    dispatch(
      updateFieldCreator({ name: group, value: { ...state[group], [name]: option?.value } }),
    );
    if (errors[errorName]) dispatch(removeError(clearErrors(errors, errorName)));
  };

  const handleAddCertificationBody = (cb) => {
    const newCertificationBodyLevelDomains = { ...state.certificationBodyLevelDomains };
    newCertificationBodyLevelDomains[cb] = [];
    dispatch(
      updateFieldCreator({
        name: 'certificationBodyLevelDomains',
        value: { ...newCertificationBodyLevelDomains },
      }),
    );
  };

  const handleRemoveCertificationBody = (cb) => {
    const newCertificationBodyLevelDomains = { ...state.certificationBodyLevelDomains };
    delete newCertificationBodyLevelDomains[cb];
    dispatch(
      updateFieldCreator({
        name: 'certificationBodyLevelDomains',
        value: { ...newCertificationBodyLevelDomains },
      }),
    );
  };

  const handleUpdateCertificationBodyLevelDomain = (cb) => (option) => {
    const newCertificationBodyLevelDomains = { ...state.certificationBodyLevelDomains };
    newCertificationBodyLevelDomains[cb] = option.map((option) => option.value);
    dispatch(
      updateFieldCreator({
        name: 'certificationBodyLevelDomains',
        value: { ...newCertificationBodyLevelDomains },
      }),
    );
  };

  useEffect(() => {
    loadOrganizationsAttributes();
  }, []);

  const submit = () => {
    const params = {
      type: state.type,
      size: state.size,
      industries: state.industries,
      country: state.country,
      name: state.name,
      address: {
        line1: state.address?.line1,
        line2: state.address?.line2,
        postCode: state.address?.postCode,
        city: state.address?.city,
        country: state.address?.country,
      },
      status: state.status,
      contactPerson: state.contactPerson,
      contactEmail: state.contactEmail,
      invoiceAddressSame: state.invoiceAddressSame,
    };

    if (!state.invoiceAddressSame) {
      params.invoiceAddress = {
        line1: state.invoiceAddress?.line1,
        line2: state.invoiceAddress?.line2,
        postCode: state.invoiceAddress?.postCode,
        city: state.invoiceAddress?.city,
        country: state.invoiceAddress?.country,
      };
    }

    if (state.type === 'GC' || state.type === 'Both') {
      params.agreementSignedDate = state.agreementSignedDate
        ? moment(state.agreementSignedDate).format('YYYY-MM-DD')
        : null;
      params.kamUser = state.kamUser;
      params.certificationBody = state.certificationBody;
      params.certificationBodyLevelDomains = {};
      Object.entries(state.certificationBodyLevelDomains).forEach(([certKey, domains]) => {
        params.certificationBodyLevelDomains[certKey] = Array.isArray(domains) ? [...domains] : [];
      });
    }

    updateOrganization(state.id, params, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.organizations.editOrganization')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 3 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="type"
                  label={t('page.user.cis.organizations.type')}
                  required
                  error={errors.type}
                >
                  <Margin top="8" as={Row} style={{ width: '100%' }}>
                    {organizationTypeOptions.map((type) => (
                      <Col key={`type-${type.value}`} size={{ xs: 1 / 2, md: 1 / 4, xl: 1 / 6 }}>
                        <Margin bottom="8">
                          <Radio
                            onClick={() => handleChangeCustom('type', 'type')(type.value)}
                            selected={state.type === type.value}
                          >
                            {type.label}
                          </Radio>
                        </Margin>
                      </Col>
                    ))}
                  </Margin>
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="size"
                  label={t('page.user.cis.organizations.size')}
                  value={
                    organizationSizeOptions.find((option) => option.value === state.size) || ''
                  }
                  clear={() => handleChangeSelect('size')(null)}
                  required
                  error={errors.size}
                >
                  <Select
                    id="size"
                    type="text"
                    placeholder={t('page.user.cis.organizations.sizePlaceholder')}
                    value={
                      organizationSizeOptions.find((option) => option.value === state.size) || ''
                    }
                    options={organizationSizeOptions}
                    onChange={handleChangeSelect('size', 'size')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="industries"
                  label={t('page.user.cis.organizations.industries')}
                  value={
                    organizationIndustryOptions.find(
                      (option) => option.value === state.industries,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('industries')(null)}
                  required
                  error={errors.industries}
                >
                  <Select
                    id="industries"
                    type="text"
                    placeholder={t('page.user.cis.organizations.industriesPlaceholder')}
                    value={
                      organizationIndustryOptions.find(
                        (option) => option.value === state.industries,
                      ) || ''
                    }
                    options={organizationIndustryOptions}
                    onChange={handleChangeSelect('industries', 'industries')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="country"
                  label={t('page.user.cis.organizations.country')}
                  value={countries.find((option) => option.value === state.country) || ''}
                  clear={() => handleChangeSelect('country')(null)}
                  error={errors.country}
                  required
                >
                  <Select
                    id="country"
                    type="text"
                    placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                    value={countries.find((option) => option.value === state.country) || ''}
                    options={countries}
                    onChange={handleChangeSelect('country', 'country')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 3 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="name"
                  label={t('page.user.cis.organizations.name')}
                  required
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    placeholder={t('page.user.cis.organizations.namePlaceholder')}
                    value={state.name}
                    onChange={handleChangeNative('name', 'name')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
            <Margin bottom="24">
              <Heading size="h5">{t('page.user.cis.organizations.address')}</Heading>
            </Margin>
            <Row>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-line1"
                    label={t('global.addressLineLabel1')}
                    required
                    error={errors.address?.line1}
                  >
                    <input
                      id="address-line1"
                      type="text"
                      placeholder={t('global.addressLinePlaceholder1')}
                      value={state.address?.line1}
                      onChange={handleChangeNestedNative('address', 'line1', 'address.line1')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-line2"
                    label={t('global.addressLineLabel2')}
                    error={errors.address?.line2}
                  >
                    <input
                      id="address-line2"
                      type="text"
                      placeholder={t('global.addressLinePlaceholder2')}
                      value={state.address?.line2}
                      onChange={handleChangeNestedNative('address', 'line2', 'address.line2')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-city"
                    label={t('global.cityPlaceholder')}
                    required
                    error={errors.address?.city}
                  >
                    <input
                      id="address-city"
                      type="text"
                      placeholder={t('global.cityPlaceholder')}
                      value={state.address?.city}
                      onChange={handleChangeNestedNative('address', 'city', 'address.city')}
                    />
                  </Field>
                </Margin>
              </Col>
            </Row>
            <Row>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-post-code"
                    label={t('global.postCodeLabel')}
                    required
                    error={errors.address?.postCode}
                  >
                    <input
                      id="address-post-code"
                      type="text"
                      placeholder={t('global.postCodePlaceholder')}
                      value={state.address?.postCode}
                      onChange={handleChangeNestedNative('address', 'postCode', 'address.postCode')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-country"
                    label={t('page.user.cis.organizations.country')}
                    value={
                      countries.find((option) => option.value === state.address?.country) || ''
                    }
                    clear={() =>
                      handleChangeNestedSelect('address', 'country', 'address.country')(null)
                    }
                    required
                    error={errors.address?.country}
                  >
                    <Select
                      id="address-country"
                      type="text"
                      placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                      value={
                        countries.find((option) => option.value === state.address?.country) || ''
                      }
                      options={countries}
                      onChange={handleChangeNestedSelect('address', 'country', 'address.country')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="invoiceAddressSame"
                    label={t('page.user.cis.organizations.invoiceAddressSame')}
                    value={state.invoiceAddressSame}
                    error={errors.invoiceAddressSame}
                  >
                    <Checkbox
                      id="invoiceAddressSame"
                      selected={state.invoiceAddressSame}
                      onChange={handleChangeCustom('invoiceAddressSame', 'invoiceAddressSame')}
                    ></Checkbox>
                  </Field>
                </Margin>
              </Col>
            </Row>
          </Margin>
          {!state.invoiceAddressSame && (
            <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
              <Margin bottom="24">
                <Heading size="h5">{t('page.user.cis.organizations.invoiceAddress')}</Heading>
              </Margin>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-line1"
                      label={t('global.addressLineLabel1')}
                      required
                      error={errors.invoiceAddress?.line1}
                    >
                      <input
                        id="invoiceAddress-line1"
                        type="text"
                        placeholder={t('global.addressLinePlaceholder1')}
                        value={state.invoiceAddress?.line1}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'line1',
                          'invoiceAddress.line1',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-line2"
                      label={t('global.addressLineLabel2')}
                      error={errors.invoiceAddress?.line2}
                    >
                      <input
                        id="invoiceAddress-line2"
                        type="text"
                        placeholder={t('global.addressLinePlaceholder2')}
                        value={state.invoiceAddress?.line2}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'line2',
                          'invoiceAddress.line2',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-city"
                      label={t('global.cityPlaceholder')}
                      required
                      error={errors.invoiceAddress?.city}
                    >
                      <input
                        id="invoiceAddress-city"
                        type="text"
                        placeholder={t('global.cityPlaceholder')}
                        value={state.invoiceAddress?.city}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'city',
                          'invoiceAddress.city',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-post-code"
                      label={t('global.postCodeLabel')}
                      error={errors.invoiceAddress?.postCode}
                    >
                      <input
                        id="invoiceAddress-post-code"
                        type="text"
                        placeholder={t('global.postCodePlaceholder')}
                        value={state.invoiceAddress?.postCode}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'postCode',
                          'invoiceAddress.postCode',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-country"
                      label={t('page.user.cis.organizations.country')}
                      value={
                        countries.find(
                          (option) => option.value === state.invoiceAddress?.country,
                        ) || ''
                      }
                      clear={() =>
                        handleChangeNestedSelect(
                          'invoiceAddress',
                          'country',
                          'invoiceAddress.country',
                        )(null)
                      }
                      required
                      error={errors.invoiceAddress?.country}
                    >
                      <Select
                        id="invoiceAddress-country"
                        type="text"
                        placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                        value={
                          countries.find(
                            (option) => option.value === state.invoiceAddress?.country,
                          ) || ''
                        }
                        options={countries}
                        onChange={handleChangeNestedSelect(
                          'invoiceAddress',
                          'country',
                          'invoiceAddress.country',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
            </Margin>
          )}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="status"
                  label={t('page.user.cis.organizations.status')}
                  value={
                    organizationStatusOptions.find((option) => option.value === state.status) || ''
                  }
                  error={errors.status}
                >
                  <Select
                    id="status"
                    type="text"
                    placeholder={t('page.user.cis.organizations.statusPlaceholder')}
                    value={
                      organizationStatusOptions.find((option) => option.value === state.status) ||
                      ''
                    }
                    options={organizationSizeOptions}
                    disabled
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactPerson"
                  label={t('page.user.cis.organizations.contactPerson')}
                  error={errors.contactPerson}
                >
                  <input
                    id="contactPerson"
                    type="text"
                    placeholder={t('page.user.cis.organizations.contactPersonPlaceholder')}
                    value={state.contactPerson}
                    onChange={handleChangeNative('contactPerson', 'contactPerson')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactEmail"
                  label={t('page.user.cis.organizations.contactEmail')}
                  error={errors.contactEmail}
                >
                  <input
                    id="contactEmail"
                    type="text"
                    placeholder={t('page.user.cis.organizations.contactEmailPlaceholder')}
                    value={state.contactEmail}
                    onChange={handleChangeNative('contactEmail', 'contactEmail')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          {(state.type === 'GC' || state.type === 'Both') && (
            <>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="agreementSignedDate"
                      label={t('page.user.cis.organizations.agreementSignedDate')}
                      value={state.agreementSignedDate}
                      clear={() => handleChangeCustom('agreementSignedDate')(null)}
                      error={errors.agreementSignedDate}
                    >
                      <Datepicker
                        id="agreementSignedDate"
                        type="text"
                        small
                        placeholderText={t(
                          'page.user.cis.organizations.agreementSignedDatePlaceholder',
                        )}
                        selected={state.agreementSignedDate}
                        onChange={handleChangeCustom('agreementSignedDate', 'agreementSignedDate')}
                        dateFormat="yyyy-MM-dd"
                        showYearDropdown
                        showMonthDropdown
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="kamUser"
                      label={t('page.user.cis.organizations.kamUser')}
                      required
                      error={errors.kamUser}
                    >
                      <DebouncedSelect
                        id="kamUser"
                        value={state.kamUser}
                        onChange={handleChangeSelect('kamUser', 'kamUser')}
                        placeholder={t('page.user.cis.organizations.kamUserPlaceholder')}
                        intialSelectedOption={state.selectedkamUser}
                        fetchOptions={getCisUsersOptions}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 3 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="certificationBody"
                      label={t('page.user.cis.organizations.certificationBody')}
                      value={
                        certificationBodies.find(
                          (option) => option.value === state.certificationBody,
                        ) || ''
                      }
                      clear={() => handleChangeSelect('certificationBody')(null)}
                      error={errors.certificationBody}
                    >
                      <Select
                        id="certificationBody"
                        type="text"
                        placeholder={t('page.user.cis.organizations.certificationBodyPlaceholder')}
                        value={
                          certificationBodies.find(
                            (option) => option.value === state.certificationBody,
                          ) || ''
                        }
                        options={certificationBodies}
                        onChange={handleChangeSelect('certificationBody', 'certificationBody')}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
              <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
                <Row style={{ alignItems: 'center' }}>
                  <Col size={{ xl: 9 / 10 }}>
                    <Select
                      id="tempCertificationBody"
                      type="text"
                      placeholder={t('page.user.cis.organizations.certificationBodyPlaceholder')}
                      value={
                        certificationBodies.find(
                          (option) => option.value === state.tempCertificationBody,
                        ) || ''
                      }
                      options={certificationBodies.filter(
                        (cb) => !(cb.value in state.certificationBodyLevelDomains),
                      )}
                      onChange={handleChangeSelect(
                        'tempCertificationBody',
                        'tempCertificationBody',
                      )}
                    />
                  </Col>
                  <Col size={{ xl: 1 / 10 }}>
                    <ButtonIcon
                      disabled={!state.tempCertificationBody}
                      type="button"
                      icon="add"
                      secondary
                      onClick={() => {
                        handleAddCertificationBody(state.tempCertificationBody);
                      }}
                      style={{ width: '45px', height: '45px' }}
                    />
                  </Col>
                </Row>
                <div style={{ padding: '50px' }}>
                  {Object.entries(state.certificationBodyLevelDomains).map(([certKey, domains]) => (
                    <Margin bottom={{ xl: 20 }}>
                      <Row key={certKey} style={{ alignItems: 'center' }}>
                        <Col size={{ xl: 1 / 10 }}>
                          <ButtonIcon
                            type="button"
                            icon="remove"
                            onClick={() => {
                              handleRemoveCertificationBody(certKey);
                            }}
                            style={{ width: '45px', height: '45px' }}
                          />
                        </Col>
                        <Col size={{ xl: 4 / 10 }}>
                          <Heading as="h6" size="h6">
                            {certificationBodies.find((x) => x.value == certKey)?.label}
                          </Heading>
                        </Col>
                        <Col size={{ xl: 5 / 10 }}>
                          <Select
                            isMulti={true}
                            options={levelDomains}
                            value={levelDomains.filter((option) => domains.includes(option.value))}
                            onChange={handleUpdateCertificationBodyLevelDomain(certKey)}
                          />
                        </Col>
                      </Row>
                    </Margin>
                  ))}
                </div>
              </Margin>
            </>
          )}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditOrganization.propTypes = {
  organization: PropTypes.shape({
    type: PropTypes.string,
    size: PropTypes.string,
    industries: PropTypes.string,
    country: PropTypes.number,
    name: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      postCode: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.number,
    }),
    invoiceAddressSame: PropTypes.bool,
    invoiceAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      postCode: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.number,
    }),
    status: PropTypes.bool,
    contactPerson: PropTypes.string,
    contactEmail: PropTypes.string,
    agreementSignedDate: PropTypes.any,
    kamUser: PropTypes.string,
    certificationBody: PropTypes.number,
    certificationBodyLevelDomains: PropTypes.any,
  }),
  errors: PropTypes.shape({
    type: PropTypes.string,
    size: PropTypes.string,
    industries: PropTypes.string,
    country: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    invoiceAddress: PropTypes.string,
    status: PropTypes.string,
    contactPerson: PropTypes.string,
    contactEmail: PropTypes.string,
    agreementSignedDate: PropTypes.string,
    kamUser: PropTypes.string,
    certificationBody: PropTypes.string,
    certificationBodyLevelDomains: PropTypes.string,
  }),
  createOrganization: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  certificationBodies: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
};

EditOrganization.defaultProps = {
  organization: {},
  errors: {},
};

const mapStateToProps = ({ ipma, globals }) => ({
  organization: ipma.cis.organizations.profile,
  certificationBodyLevelDomains: ipma.cis.organizations.certificationBodyLevelDomains,
  errors: ipma.cis.organizations.editErrors,
  countries: globals.countries,
  certificationBodies: globals.cis.certificationBodies,
  levelDomains: globals.cis.levelDomains,
});

const mapDispatchToProps = (dispatch) => ({
  updateOrganization: (id, payload, history) =>
    dispatch(updateIpmaCisCreator(id, 'organizations', payload, history)),
  loadCertificationBodyLevelDomains: (payload) =>
    dispatch(loadIpmaCisOrganizationCertificationBodyLevelDomainsCreator(payload)),
  removeCertificationBodyLevelDomains: (payload) =>
    dispatch(removeIpmaCisOrganizationCertificationBodyLevelDomainsCreator(payload)),
  removeError: (payload) => dispatch(setIpmaCisEditErrorsCreator('organizations', payload)),
  loadOrganizationsAttributes: () => dispatch(loadGlobalCisOrganizationsAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditOrganization);
